/**
 * OIDC client configuration and helpers (R10.1).
 *
 * Uses ``oidc-client-ts`` to drive Authorization Code + PKCE against
 * the Keycloak realm declared in ``infra/keycloak/bootstrap.sh``.
 *
 * The ``UserManager`` is constructed lazily so the module is safe to
 * import in demo mode (where the IdP is unreachable).
 */
import {
  UserManager,
  WebStorageStateStore,
  type User,
} from "oidc-client-ts";

import { ANC_OIDC_CONFIG } from "../config";

let cachedManager: UserManager | null = null;

function getManager(): UserManager {
  if (cachedManager === null) {
    cachedManager = new UserManager({
      authority: ANC_OIDC_CONFIG.authority,
      client_id: ANC_OIDC_CONFIG.clientId,
      redirect_uri: ANC_OIDC_CONFIG.redirectUri,
      post_logout_redirect_uri: ANC_OIDC_CONFIG.postLogoutRedirectUri,
      response_type: "code",
      scope: "openid profile email",
      loadUserInfo: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: true,
    });
  }
  return cachedManager;
}

export const oidc = {
  events: {
    addUserLoaded(handler: (user: User) => void): void {
      getManager().events.addUserLoaded(handler);
    },
    removeUserLoaded(handler: (user: User) => void): void {
      getManager().events.removeUserLoaded(handler);
    },
    addUserUnloaded(handler: () => void): void {
      getManager().events.addUserUnloaded(handler);
    },
    removeUserUnloaded(handler: () => void): void {
      getManager().events.removeUserUnloaded(handler);
    },
  },
};

export async function login(): Promise<void> {
  await getManager().signinRedirect();
}

export async function logout(): Promise<void> {
  await getManager().signoutRedirect();
}

export async function handleRedirectCallback(): Promise<User> {
  return getManager().signinRedirectCallback();
}

export async function getUser(): Promise<User | null> {
  return getManager().getUser();
}

export async function getAccessToken(): Promise<string | null> {
  const user = await getManager().getUser();
  if (!user || user.expired) {
    return null;
  }
  return user.access_token;
}

export type Role =
  | "Gerente"
  | "Comercial"
  | "Operador"
  | "Analista_NRW"
  | "IngenieroRF"
  | "AnalistaFugas"
  | "Inspector"
  | "Supervisor"
  | "Admin"
  | "Regulatorio";

/** Todos los roles válidos del sistema, en orden de presentación. */
export const ALL_VALID_ROLES: Role[] = [
  "Gerente",
  "Comercial",
  "Operador",
  "Analista_NRW",
  "IngenieroRF",
  "AnalistaFugas",
  "Inspector",
  "Supervisor",
  "Admin",
  "Regulatorio",
];

/** Metadata de presentación por rol: etiqueta legible, descripción y ruta de inicio. */
export const ROLE_META: Record<
  Role,
  { label: string; description: string; home: string }
> = {
  Gerente: {
    label: "Gerente / Ejecutivo",
    description: "Visión estratégica: ANF global, ROI, recuperación económica.",
    home: "/",
  },
  Comercial: {
    label: "Gerente Comercial",
    description: "Pérdidas comerciales, fraude, submedición y recuperación de facturación.",
    home: "/comercial",
  },
  Operador: {
    label: "Operador",
    description: "Monitoreo de consumo por DMA y dashboards.",
    home: "/operador/dashboards",
  },
  Analista_NRW: {
    label: "Analista NRW / ANF",
    description: "Balance hídrico por DMA, residual, forecast y simulaciones.",
    home: "/analista/balance",
  },
  IngenieroRF: {
    label: "Ingeniero RF / Telemetría",
    description: "Salud de red LoRaWAN, medidores mudos, cobertura y gateways.",
    home: "/lora",
  },
  AnalistaFugas: {
    label: "Analista de Fugas / Cliente",
    description: "Fugas domiciliarias por consumo nocturno y avisos al cliente.",
    home: "/fugas",
  },
  Inspector: {
    label: "Inspector de Terreno",
    description: "Órdenes asignadas, captura de evidencia y resultados.",
    home: "/inspector/ordenes",
  },
  Supervisor: {
    label: "Supervisor de Operaciones",
    description: "Cuadrillas, SLA, priorización de órdenes y catastro.",
    home: "/casos",
  },
  Admin: {
    label: "Administrador / TI",
    description: "Plataforma, integraciones, observabilidad, usuarios y auditoría.",
    home: "/observabilidad",
  },
  Regulatorio: {
    label: "Cumplimiento SISS",
    description: "Indicadores regulatorios, avisos de fuga y reportes auditables.",
    home: "/regulatorio",
  },
};

export interface ProfileLike {
  roles?: string[];
  realm_access?: { roles?: string[] };
}

export interface UserLike {
  profile?: ProfileLike;
}

export function rolesFromUser(user: UserLike | null | undefined): Role[] {
  if (!user || !user.profile) return [];
  const direct = Array.isArray(user.profile.roles) ? user.profile.roles : [];
  const realm = Array.isArray(user.profile.realm_access?.roles)
    ? (user.profile.realm_access?.roles as string[])
    : [];
  const candidates = new Set<string>([...direct, ...realm]);
  return ALL_VALID_ROLES.filter((role) => candidates.has(role));
}
